import { useState, useEffect } from 'react';
import { saveAs } from "file-saver";
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import FeedIcon from '@mui/icons-material/Feed';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GstReportLoader from '../../common/GstReportLoader';
import { useHistory } from 'react-router-dom';

function Salegstreport() {
    const [isLoading, setIsLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { isAuthenticated, companyId, token, logout } = useAuth();
    const history = useHistory();

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });
    const closeSnackbar = () =>
        setSnackbar(s => ({ ...s, open: false }));

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };


    const fetchTableDataBTOB = async () => {
        const response = await fetchData('ReportApi', `/SaleGstBtoB/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '0000') throw new Error('Failed to fetch BTOB data');
        return response.response_data;
    };

    const fetchTableDataBTOC = async () => {
        const response = await fetchData('ReportApi', `/SaleGstBtoC/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '0000') throw new Error('Failed to fetch BTOC data');
        return response.response_data;
    };

    const fetchTableDataBTOBRTN = async () => {
        const response = await fetchData('ReportApi', `/SaleGstRTNBtoB/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '0000') throw new Error('Failed to fetch BTOB Return data');
        return response.response_data;
    };

    const fetchTableDataBTOCRTN = async () => {
        const response = await fetchData('ReportApi', `/SaleGstRTNBtoC/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '0000') throw new Error('Failed to fetch BTOC Return data');
        return response.response_data;
    };

    const fetchTableDataHSNBtoB = async () => {
        const response = await fetchData('ReportApi', `/SaleGstHSNBtoB/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '0000') throw new Error('Failed to fetch HSN BtoB data');
        return response.response_data;
    };

    const fetchTableDataHSNBtoC = async () => {
        const response = await fetchData('ReportApi', `/SaleGstHSNBtoC/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '0000') throw new Error('Failed to fetch HSN BtoC data');
        return response.response_data;
    };


    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const getMonthYear = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
    };

    const handleExportExcel = async () => {
        if (new Date(fromDate) > new Date(toDate)) {
            setSnackbar({ open: true, message: 'From Date cannot be greater than To Date', severity: 'error' });
            return;
        }
        setIsLoading(true);
        try {
            // Fetch all API data + template in parallel
            const [btobData, btocData, hsnDataBtoB, hsnDataBtoC, btobDatartn, btocDatartn, templateRes] = await Promise.all([
                fetchTableDataBTOB().catch(() => []),
                fetchTableDataBTOC().catch(() => []),
                fetchTableDataHSNBtoB().catch(() => []),
                fetchTableDataHSNBtoC().catch(() => []),
                fetchTableDataBTOBRTN().catch(() => []),
                fetchTableDataBTOCRTN().catch(() => []),
                // Allow browser to cache the template — no need to re-download every time
                fetch('/SaleGSTTemplate/SaleGSTExport.xlsx'),
            ]);

            const templateBuffer = await templateRes.arrayBuffer();

            // Hand off ALL heavy ExcelJS work to a Web Worker so the main thread stays free
            const worker = new Worker('/workers/saleGstWorker.js');

            // Do NOT transfer templateBuffer — transferring detaches it before SheetJS can read it
            worker.postMessage(
                { templateBuffer, btobData, btocData, hsnDataBtoB, hsnDataBtoC, btobDatartn, btocDatartn, toDate }
            );

            worker.onmessage = (event) => {
                worker.terminate();
                const { success, buffer, error } = event.data;
                if (!success) {
                    console.error('Worker error:', error);
                    showSnackbar('Export failed: ' + error, 'error');
                } else {
                    const blob = new Blob([new Uint8Array(buffer)], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    saveAs(blob, `Sale GST(${getMonthYear(toDate)}).xlsx`);
                    showSnackbar('Excel file exported successfully', 'success');
                }
                setIsLoading(false);
            };

            worker.onerror = (err) => {
                worker.terminate();
                console.error('Worker error:', err);
                showSnackbar('Export failed. Please try again.', 'error');
                setIsLoading(false);
            };

        } catch (error) {
            console.error('Export error:', error);
            showSnackbar('Export failed. Please try again.', 'error');
            setIsLoading(false);
        }
    };


    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <GstReportLoader open={isLoading} message="Generating GST Report..." />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MuiAlert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    elevation={6}
                    style={{ fontSize: '13px', borderRadius: '8px', minWidth: '260px' }}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #1c2266 0%, #3949ab 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Button
                        onClick={() => history.push('/ReportsHub')}
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        style={{ color: '#fff', textTransform: 'none', fontSize: '12px', padding: '4px 8px', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', minWidth: 'auto' }}
                    >
                        
                    </Button>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Sale GST Report</span>
                </div>
                <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <label style={{ color: '#1c2266', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }} htmlFor="fromDate">
                            From Date
                        </label>
                        <input
                            id="fromDate"
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{
                                padding: '5px 8px',
                                border: '1px solid #c5cfe0',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#333',
                                outline: 'none',
                                background: '#f8fafc',
                                cursor: 'pointer',
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <label style={{ color: '#1c2266', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }} htmlFor="toDate">
                            To Date
                        </label>
                        <input
                            id="toDate"
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            style={{
                                padding: '5px 8px',
                                border: '1px solid #c5cfe0',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#333',
                                outline: 'none',
                                background: '#f8fafc',
                                cursor: 'pointer',
                            }}
                        />
                    </div>
                    <Button
                        onClick={handleExportExcel}
                        disabled={isLoading}
                        style={{
                            backgroundColor: '#1c2266',
                            color: 'white',
                            fontWeight: '600',
                            textTransform: 'none',
                            fontSize: '12px',
                            padding: '5px 16px',
                            borderRadius: '6px',
                            boxShadow: '0 2px 6px rgba(28,34,102,0.25)',
                            opacity: isLoading ? 0.7 : 1,
                            whiteSpace: 'nowrap',
                            height: '29px',
                        }}
                    >
                        <FeedIcon style={{ fontSize: '15px', marginRight: '5px' }} />
                        Export to Excel
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default Salegstreport;


